import React, { RefObject } from 'react'
import { useTranslation } from 'next-i18next'
import { TailSpin } from 'react-loader-spinner'

import { LocationUnit, LocationUnitId } from 'state/ducks/locationUnit/models'

import useCheckBreakpoint from 'lib/checkBreakpoint'
import useScrollPositionRestoration from 'hooks/common/useScrollPositionRestoration'

import styles from './LocationUnitCardList.module.scss'
import LocationUnitCard from 'components/domains/locationUnit/LocationUnitCard'

interface Props {
  onPressSearchFilterButton: () => void
  locationUnits: Array<LocationUnit>
  selectedLocationUnitId: LocationUnitId
  locationName: string
  isLoading: boolean
  itemRefs?: RefObject<(HTMLLIElement | null)[]>
  isFiltered: boolean
}

const LocationUnitCardList: React.FC<Props> = ({
  onPressSearchFilterButton,
  locationUnits,
  selectedLocationUnitId,
  locationName,
  isLoading,
  itemRefs,
  isFiltered,
}: Props): JSX.Element => {
  const { t } = useTranslation()
  const { isMobile } = useCheckBreakpoint()
  const scrollRef = useScrollPositionRestoration<HTMLDivElement>(locationUnits.length > 0)

  return (
    <div ref={scrollRef} className={styles.cardList}>
      <div className={styles.cardListHeading}>
        {isMobile && (
          <div className={styles.cardListHeadingTop}>
            <p className={styles.totalSpace}>
              {t('SPACES.HEADING.TOTAL_SPACE', {
                spaceCount: locationUnits.length,
              })}
            </p>
          </div>
        )}
        <div className={styles.cardListHeadingBottom}>
          <h1 className={styles.title}>
            {t('SPACES.HEADING.TITLE', {
              location: locationName || t('SPACES.HEADING.DEFAULT_PREFIX_TITLE'),
            })}
          </h1>
          <p className={styles.description}>
            {t('SPACES.HEADING.DESCRIPTION', {
              location: locationName || t('SPACES.HEADING.DEFAULT_PREFIX_TITLE'),
            })}
          </p>
          {!isMobile && (
            <div className={styles.totalFilterBox}>
              <p className={styles.totalSpace}>
                {t('SPACES.HEADING.TOTAL_SPACE', {
                  spaceCount: locationUnits.length,
                })}
              </p>
              <div
                className={`${styles.filterButton} ${isFiltered ? styles.filterButtonActive : ''}`}
                onClick={onPressSearchFilterButton}
              >
                <div className={styles.buttonText}>
                  {isFiltered ? t('COMMON.FILTERING') : t('COMMON.FILTER')}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {!isLoading ? (
        <>
          {locationUnits.length !== 0 ? (
            <ul className={styles.resultSpaceList}>
              {locationUnits.map(
                (unit, index) =>
                  unit &&
                  (itemRefs ? (
                    <LocationUnitCard
                      unit={unit}
                      selected={
                        selectedLocationUnitId.type === unit.type &&
                        selectedLocationUnitId.encryptedId === unit.encryptedId
                      }
                      encryptedId={unit.encryptedId}
                      key={index}
                      ref={(el) => (itemRefs.current[index] = el)}
                    />
                  ) : (
                    <LocationUnitCard
                      unit={unit}
                      selected={
                        selectedLocationUnitId.type === unit.type &&
                        selectedLocationUnitId.encryptedId === unit.encryptedId
                      }
                      encryptedId={unit.encryptedId}
                      key={index}
                    />
                  ))
              )}
            </ul>
          ) : (
            <div className={styles.notMatchingResult}>
              <img src="/not_matching_space_icon.svg" alt={t('META.NOT_MATCHING_SPACE_ICON')} />
              <p className={styles.notMatchingResultErrorMessage}>
                {t('SPACES.SIDEBAR.NOT_MATCHING_SPACE')}
              </p>
            </div>
          )}
        </>
      ) : (
        <div className={styles.loading}>
          <TailSpin height="100" width="100" color="#0b6ab3" ariaLabel="loading" />
        </div>
      )}
    </div>
  )
}

export default LocationUnitCardList
